import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Ticker from "../components/Ticker";
import Footer from "../components/Footer";
import { CATEGORIES, COVER_LIBRARY } from "../mock";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { UploadCloud, FileText, Music, Video, ArrowRight, Loader2, Check, ImageIcon, Users, Eye, Newspaper, Mail, TrendingUp, HardDrive, Archive, PlayCircle, Headphones, RefreshCw, Trash2, Pin, PinOff, Lock, Unlock, ArrowUp, Send, Undo2, ShieldCheck, ShieldAlert, Radio, AlertTriangle, Sparkles, FileDown } from "lucide-react";
import { toast } from "sonner";
import { ArticlesAPI, AudioAPI, api, normalizeArticle, ContentControlsAPI } from "../lib/api";
import { AudioPlayerModal } from "../components/AudioPlayerModal";
import { useAuth } from "../lib/auth";

const StatCard = ({ icon: Icon, label, value, sub }) => (
  <div className="border border-neutral-300 bg-white p-5">
    <div className="flex items-center justify-between">
      <span className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-semibold">{label}</span>
      <Icon className="w-4 h-4 text-[#0891B2]" />
    </div>
    <div className="mt-3 font-serif-display text-[36px] font-bold text-[#0B1F33] leading-none">{value}</div>
    {sub && <div className="mt-1 text-[11px] text-neutral-500 font-mono-tick">{sub}</div>}
  </div>
);

const ArchivePanel = () => {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => api.get("/admin/archive/status").then((r) => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/archive/run");
      const n = (r.data.archived || []).length;
      toast.success(n === 0 ? "Archive scan complete — no eligible articles yet." : `${n} article${n > 1 ? "s" : ""} archived to Dropbox.`);
      load();
    } catch (e) {
      toast.error("Archive run failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mb-12 border border-neutral-300 bg-white p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-semibold flex items-center gap-1.5">
            <HardDrive className="w-3.5 h-3.5" /> Dropbox Archive
          </div>
          <h3 className="mt-1 font-serif-display text-[22px] font-bold text-[#0B1F33]">Rotation & Storage</h3>
          {status?.configured ? (
            <div className="mt-2 text-[13px] text-neutral-700 leading-relaxed">
              Connected to <span className="font-semibold">{status.dropboxAccount?.email || "Dropbox"}</span>
              {status.dropboxAccount?.type && <span className="ml-1 text-[11px] uppercase tracking-wider text-[#0891B2]">({status.dropboxAccount.type})</span>}.
              Articles older than <span className="font-semibold">{status.retentionDays} days</span> are auto-archived daily.
            </div>
          ) : (
            <div className="mt-2 text-[13px] text-neutral-600">Dropbox not configured. Set <code className="bg-neutral-100 px-1">DROPBOX_ACCESS_TOKEN</code> in backend .env.</div>
          )}
        </div>
        <button
          disabled={running || !status?.configured}
          onClick={runNow}
          className="inline-flex items-center px-4 py-2 bg-[#0B1F33] hover:bg-[#132f57] disabled:opacity-50 text-[#FFFFFF] uppercase tracking-wider text-[11px] font-semibold transition-colors"
        >
          {running ? <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Running…</> : <><PlayCircle className="w-3.5 h-3.5 mr-2" /> Run Archive Now</>}
        </button>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border border-neutral-200 p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#0891B2] font-semibold">Total Archived</div>
          <div className="mt-2 font-serif-display text-[28px] font-bold text-[#0B1F33]">{status?.totalArchived ?? "—"}</div>
        </div>
        <div className="border border-neutral-200 p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#0891B2] font-semibold">Retention Window</div>
          <div className="mt-2 font-serif-display text-[28px] font-bold text-[#0B1F33]">{status?.retentionDays ?? 21} <span className="text-[14px] text-neutral-500">days</span></div>
        </div>
        <div className="border border-neutral-200 p-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#0891B2] font-semibold">Schedule</div>
          <div className="mt-2 font-serif-display text-[15px] font-semibold text-[#0B1F33] leading-tight">Daily · 24h loop<br /><span className="text-[11px] font-normal text-neutral-500">Runs 60s after backend start, then every 24h</span></div>
        </div>
      </div>

      {status?.recent?.length > 0 && (
        <div className="mt-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-semibold mb-2 flex items-center gap-1.5"><Archive className="w-3 h-3" /> Recently Archived</div>
          <ul className="divide-y divide-neutral-200 border border-neutral-200">
            {status.recent.map((r) => (
              <li key={r.id} className="px-3 py-2 flex items-center justify-between text-[13px]">
                <span className="truncate text-[#0B1F33]">{r.title || r.slug}</span>
                <span className="ml-3 font-mono-tick text-[10px] text-neutral-500 shrink-0">{r.dropboxPath}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const StatsPanel = () => {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    const t = setInterval(() => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}), 30000);
    return () => clearInterval(t);
  }, []);

  if (!stats) {
    return (
      <div className="mb-10 p-6 border border-neutral-300 bg-white flex items-center justify-center text-neutral-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2 text-[#0891B2]" /> Loading statistics…
      </div>
    );
  }

  const maxDaily = Math.max(1, ...(stats.visits.daily || []).map((d) => d.count));

  return (
    <div className="mb-12">
      <div className="mb-4 pb-3 border-b-2 border-[#0B1F33] flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-semibold">Administrative</div>
          <h2 className="mt-1 font-serif-display text-[26px] font-bold text-[#0B1F33]">Traffic & Content</h2>
        </div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-400 font-mono-tick">Auto-refresh 30s</div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        <StatCard icon={Eye} label="Today" value={stats.visits.today.toLocaleString()} sub="page views" />
        <StatCard icon={TrendingUp} label="Last 7 days" value={stats.visits.last7d.toLocaleString()} sub="page views" />
        <StatCard icon={Users} label="Unique 30d" value={stats.visits.unique30d.toLocaleString()} sub="by IP" />
        <StatCard icon={Newspaper} label="Articles" value={stats.articles.published} sub={`${stats.articles.total} total`} />
        <StatCard icon={Mail} label="Members" value={(stats.users?.total ?? 0).toLocaleString()} sub={`${stats.users?.premium ?? 0} premium`} />
        <StatCard icon={TrendingUp} label="Revenue" value={`$${((stats.revenue?.totalCents ?? 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} sub={`${stats.revenue?.paidCount ?? 0} paid`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-neutral-300 bg-white p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-semibold mb-3">Daily Traffic · Last 14 Days</div>
          {stats.visits.daily.length === 0 ? (
            <div className="py-8 text-center text-sm text-neutral-500">No traffic data yet — visitors will appear as they arrive.</div>
          ) : (
            <div className="flex items-end gap-1.5 h-32">
              {stats.visits.daily.map((d) => (
                <div key={d.day} className="flex-1 flex flex-col items-center justify-end" title={`${d.day}: ${d.count}`}>
                  <div className="w-full bg-[#0B1F33] hover:bg-[#B8E9F1] transition-colors" style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: 2 }} />
                  <div className="mt-1 text-[9px] font-mono-tick text-neutral-500">{d.day.slice(5)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border border-neutral-300 bg-white p-5">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-semibold mb-3">Top Paths · Last 7 Days</div>
          {stats.visits.topPaths.length === 0 ? (
            <div className="py-8 text-center text-sm text-neutral-500">No page-level data yet.</div>
          ) : (
            <ul className="space-y-2">
              {stats.visits.topPaths.map((p) => (
                <li key={p.path} className="flex items-center justify-between text-[13px]">
                  <span className="truncate text-[#0B1F33]">{p.path === "/" ? "Homepage" : p.path}</span>
                  <span className="font-mono-tick text-[#0891B2] font-semibold">{p.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

const MembersTabBtn = ({ id, count, label, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`members-tab-${id}`}
    className={`px-4 py-2 text-[11px] tracking-[0.22em] uppercase font-condensed font-bold border-b-2 transition-colors ${active ? "border-[#B8E9F1] text-[#0B1F33]" : "border-transparent text-neutral-500 hover:text-[#0B1F33]"}`}
  >
    {label} <span className="ml-1 text-[#0891B2]">{count}</span>
  </button>
);

const MembersPanel = () => {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState({ total: 0, users: [] });
  const [subs, setSubs] = useState({ total: 0, subscribers: [] });
  const [pays, setPays] = useState({ total: 0, paidCount: 0, pendingCount: 0, payments: [] });
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [u, s, p] = await Promise.all([
        api.get("/admin/users"),
        api.get("/admin/subscribers"),
        api.get("/admin/payments"),
      ]);
      setUsers(u.data);
      setSubs(s.data);
      setPays(p.data);
    } catch (e) {
      toast.error("Failed to load members data");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const setSubStatus = async (userId, subscriptionStatus, tier = null) => {
    try {
      await api.patch(`/admin/users/${userId}/subscription`, { subscriptionStatus, subscriptionTier: tier });
      toast.success(`User ${subscriptionStatus === "active" ? "granted premium" : subscriptionStatus}`);
      load();
    } catch (e) {
      toast.error("Update failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const exportCsv = (rows, filename) => {
    if (!rows?.length) return;
    const keys = Object.keys(rows[0]);
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => escape(r[k])).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const setTab_ = (id) => setTab(id);

  return (
    <div className="mb-12 border border-neutral-300 bg-white" data-testid="members-panel">
      <div className="p-6 border-b border-neutral-200 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-semibold flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Members & Revenue
          </div>
          <h3 className="mt-1 font-serif-display text-[22px] font-bold text-[#0B1F33]">Back Office</h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 border border-neutral-300 hover:border-[#B8E9F1] text-[11px] tracking-[0.2em] uppercase font-condensed font-bold text-[#0B1F33]"
        >
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
        </button>
      </div>

      <div className="px-6 border-b border-neutral-200 flex gap-2">
        <MembersTabBtn id="users" count={users.total} label="Registered Users" active={tab === "users"} onClick={() => setTab_("users")} />
        <MembersTabBtn id="subs" count={subs.total} label="Email Subscribers" active={tab === "subs"} onClick={() => setTab_("subs")} />
        <MembersTabBtn id="pays" count={pays.total} label="Payments" active={tab === "pays"} onClick={() => setTab_("pays")} />
      </div>

      {tab === "users" && (
        <div className="p-6">
          <div className="flex justify-end mb-3">
            <button
              onClick={() => exportCsv(users.users, "gf-users.csv")}
              className="text-[10px] tracking-[0.24em] uppercase text-[#0891B2] hover:text-[#0B1F33] font-condensed font-bold"
              data-testid="export-users-csv"
            >
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left border-b-2 border-neutral-300 text-[10px] tracking-[0.2em] uppercase text-neutral-500">
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Provider</th>
                  <th className="py-2 pr-4">Subscription</th>
                  <th className="py-2 pr-4">Joined</th>
                  <th className="py-2 pr-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.users.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center text-neutral-500">No registered users yet.</td></tr>
                )}
                {users.users.map((u) => (
                  <tr key={u.id} className="border-b border-neutral-200 hover:bg-neutral-50">
                    <td className="py-2 pr-4 font-mono-tick text-[12px] text-[#0B1F33]">{u.email}</td>
                    <td className="py-2 pr-4">{u.name || "—"}</td>
                    <td className="py-2 pr-4 text-[11px] uppercase tracking-wider text-neutral-500">{u.provider || "google"}</td>
                    <td className="py-2 pr-4">
                      {u.subscriptionStatus === "active" ? (
                        <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.2em] uppercase bg-[#B8E9F1] text-[#0B1F33] px-2 py-0.5 font-condensed font-bold">
                          <Sparkles className="w-3 h-3" /> {u.subscriptionTier || "premium"}
                        </span>
                      ) : (
                        <span className="text-[11px] uppercase tracking-wider text-neutral-500">Free</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-[11px] font-mono-tick text-neutral-500">
                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "—"}
                    </td>
                    <td className="py-2 pr-4 text-right">
                      {u.subscriptionStatus === "active" ? (
                        <button
                          onClick={() => setSubStatus(u.id, "cancelled")}
                          className="text-[10px] tracking-[0.2em] uppercase font-condensed font-bold text-neutral-500 hover:text-red-600"
                        >
                          Cancel
                        </button>
                      ) : (
                        <button
                          onClick={() => setSubStatus(u.id, "active", "comp")}
                          className="text-[10px] tracking-[0.2em] uppercase font-condensed font-bold text-[#0891B2] hover:text-[#0B1F33]"
                          data-testid={`comp-user-${u.id}`}
                        >
                          Comp Premium
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "subs" && (
        <div className="p-6">
          <div className="flex justify-end mb-3">
            <button
              onClick={() => exportCsv(subs.subscribers, "gf-subscribers.csv")}
              className="text-[10px] tracking-[0.24em] uppercase text-[#0891B2] hover:text-[#0B1F33] font-condensed font-bold"
              data-testid="export-subscribers-csv"
            >
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left border-b-2 border-neutral-300 text-[10px] tracking-[0.2em] uppercase text-neutral-500">
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Tier</th>
                  <th className="py-2 pr-4">Source</th>
                  <th className="py-2 pr-4">Joined</th>
                </tr>
              </thead>
              <tbody>
                {subs.subscribers.length === 0 && (
                  <tr><td colSpan={4} className="py-10 text-center text-neutral-500">No email captures yet.</td></tr>
                )}
                {subs.subscribers.map((s, i) => (
                  <tr key={s.email + i} className="border-b border-neutral-200 hover:bg-neutral-50">
                    <td className="py-2 pr-4 font-mono-tick text-[12px] text-[#0B1F33]">{s.email}</td>
                    <td className="py-2 pr-4 text-[11px] uppercase tracking-wider">{s.tier || "free"}</td>
                    <td className="py-2 pr-4 text-[11px] text-neutral-500">{s.source || "—"}</td>
                    <td className="py-2 pr-4 text-[11px] font-mono-tick text-neutral-500">
                      {s.createdAt ? new Date(s.createdAt).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "pays" && (
        <div className="p-6">
          <div className="flex items-center justify-between mb-3 gap-3">
            <div className="flex gap-4 text-[11px] tracking-[0.2em] uppercase text-neutral-500">
              <span>Paid <span className="text-[#0891B2] font-bold ml-1">{pays.paidCount}</span></span>
              <span>Pending <span className="text-neutral-700 font-bold ml-1">{pays.pendingCount}</span></span>
            </div>
            <button
              onClick={() => exportCsv(pays.payments, "gf-payments.csv")}
              className="text-[10px] tracking-[0.24em] uppercase text-[#0891B2] hover:text-[#0B1F33] font-condensed font-bold"
            >
              Export CSV
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left border-b-2 border-neutral-300 text-[10px] tracking-[0.2em] uppercase text-neutral-500">
                  <th className="py-2 pr-4">Session</th>
                  <th className="py-2 pr-4">Package</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {pays.payments.length === 0 && (
                  <tr><td colSpan={6} className="py-10 text-center text-neutral-500">No payments yet.</td></tr>
                )}
                {pays.payments.map((p) => (
                  <tr key={p.sessionId} className="border-b border-neutral-200 hover:bg-neutral-50">
                    <td className="py-2 pr-4 font-mono-tick text-[11px] text-neutral-500">{p.sessionId?.slice(0, 20)}…</td>
                    <td className="py-2 pr-4 text-[12px]">{p.packageId}</td>
                    <td className="py-2 pr-4 font-mono-tick text-[12px] text-[#0B1F33]">${(p.amount || 0).toFixed(2)}</td>
                    <td className="py-2 pr-4 text-[11px] font-mono-tick truncate max-w-[180px]">{p.userEmail || "—"}</td>
                    <td className="py-2 pr-4">
                      <span className={`inline-flex text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 font-condensed font-bold ${
                        p.paymentStatus === "paid" ? "bg-emerald-100 text-emerald-800" :
                        p.paymentStatus === "unpaid" ? "bg-amber-100 text-amber-800" :
                        "bg-neutral-100 text-neutral-600"
                      }`}>
                        {p.paymentStatus || p.status || "—"}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-[11px] font-mono-tick text-neutral-500">
                      {p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

const TransistorPublishAllButton = () => {
  const [running, setRunning] = useState(false);
  const publish = async () => {
    if (!window.confirm(
      "Publish every article with audio (that isn't already on Transistor) to your Vexent Wire show?\n\n" +
      "Idempotent — articles already on Transistor are skipped. Runs newest first. May take 30-90 seconds per episode."
    )) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/transistor/backfill?limit=200");
      const ok = (r.data?.published || []).length;
      const fail = (r.data?.failed || []).length;
      if (ok) toast.success(`Published ${ok} episode${ok === 1 ? "" : "s"} to Transistor.`);
      if (fail) toast.error(`${fail} failed — check server logs`);
      if (!ok && !fail) toast.info("Nothing to publish — everything is already on Transistor.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Publish failed: ${msg}`);
    } finally {
      setRunning(false);
    }
  };
  return (
    <Button
      onClick={publish}
      disabled={running}
      size="sm"
      className="rounded-none bg-[#0B1F33] hover:bg-[#132f57] text-white uppercase tracking-wider text-[10px] font-bold disabled:opacity-60"
      data-testid="transistor-publish-all"
    >
      {running ? (
        <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Publishing…</>
      ) : (
        <><Headphones className="w-3.5 h-3.5 mr-1.5" /> Publish All to Transistor</>
      )}
    </Button>
  );
};

const BulkRegenerateAudio = ({ articleCount, onDone }) => {
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState("short");
  const [status, setStatus] = useState(null); // {status, processed, total, errors}
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const r = await api.get("/admin/audio/regenerate-all/status");
      setStatus(r.data);
      if (r.data.status === "done" || r.data.status === "failed") {
        clearInterval(pollRef.current);
        pollRef.current = null;
        if (r.data.status === "done") {
          toast.success(`Regeneration complete — ${r.data.processed}/${r.data.total} articles`);
          onDone && onDone();
        } else {
          toast.error(`Regeneration failed: ${r.data.error || "see server logs"}`);
        }
      }
    } catch (e) {
      // silent
    }
  };

  const start = async () => {
    const kindLabel = { short: "short 2-3 min audio", podcast: "long-form podcast", both: "BOTH short + podcast" }[kind];
    if (!window.confirm(
      `Regenerate ${kindLabel} for ALL ${articleCount} voiced articles on THIS environment?\n\n` +
      `This will overwrite the current MP3s in this environment's storage bucket. ` +
      `Runs in the background — takes 60-90 seconds per article for short-only, ` +
      `up to 2-3 minutes per article for podcasts. Total ~${articleCount * (kind === 'both' ? 3 : kind === 'podcast' ? 2 : 1)} min.`
    )) return;
    try {
      await api.post("/admin/audio/regenerate-all", { kind, forceSummary: true, force: kind === "podcast" });
      toast.success("Bulk regeneration started. Progress updates every few seconds.");
      setOpen(true);
      pollRef.current = setInterval(fetchStatus, 4000);
      fetchStatus();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Failed to start: ${msg}`);
    }
  };

  // If a job is already running when the admin lands on this page, resume polling.
  useEffect(() => {
    fetchStatus().then(() => {
      if (status?.status === "processing" && !pollRef.current) {
        pollRef.current = setInterval(fetchStatus, 4000);
      }
    });
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const running = status?.status === "processing";
  const pct = status?.total ? Math.round((status.processed / status.total) * 100) : 0;

  return (
    <>
      <div className="flex items-center gap-1.5">
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          disabled={running}
          className="text-[10px] uppercase tracking-[0.15em] border border-neutral-300 bg-white px-2 py-1.5 font-semibold text-[#0B1F33] focus:outline-none focus:border-[#B8E9F1]"
          data-testid="bulk-regen-kind"
        >
          <option value="short">Short only</option>
          <option value="podcast">Podcast only</option>
          <option value="both">Both</option>
        </select>
        <Button
          onClick={start}
          disabled={running}
          size="sm"
          className="rounded-none bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33] uppercase tracking-wider text-[10px] font-bold disabled:opacity-60"
          data-testid="bulk-regen-start"
        >
          {running ? (
            <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Regenerating {status.processed}/{status.total}</>
          ) : (
            <><Sparkles className="w-3.5 h-3.5 mr-1.5" /> Regenerate All ({articleCount})</>
          )}
        </Button>
        <TransistorPublishAllButton />
      </div>
      {(running || status?.status === "done" || status?.status === "failed") && (
        <div className="w-full basis-full mt-3 border border-neutral-300 bg-[#FFFFFF] px-4 py-3" data-testid="bulk-regen-status">
          <div className="flex items-center justify-between mb-1">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#0891B2]">
              Bulk regeneration · {status?.status}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-600">
              {status?.processed || 0} / {status?.total || 0} ({pct}%)
            </div>
          </div>
          <div className="h-1.5 bg-neutral-200 relative overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-[#B8E9F1] transition-all" style={{ width: `${pct}%` }} />
          </div>
          {status?.errors?.length > 0 && (
            <details className="mt-2">
              <summary className="text-[11px] text-red-700 cursor-pointer">
                {status.errors.length} error{status.errors.length > 1 ? "s" : ""} (click to expand)
              </summary>
              <ul className="mt-1 text-[10px] text-red-800 space-y-1">
                {status.errors.map((e, i) => (
                  <li key={i}>[{e.kind}] {e.articleId?.slice(0, 12)}: {e.error?.slice(0, 200)}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </>
  );
};


const AudioStudioPanel = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState({}); // { [articleId]: 'processing' | 'done' | 'failed' | 'none' }
  const [playing, setPlaying] = useState(null);
  const [health, setHealth] = useState({}); // articleId -> { ok, checks }
  const [verifying, setVerifying] = useState({}); // articleId -> bool
  const [verifyingAll, setVerifyingAll] = useState(false);
  const [stats, setStats] = useState(null);
  const [voices, setVoices] = useState([]);
  const [chosenVoice, setChosenVoice] = useState({}); // articleId -> voiceId (unsaved selection)
  const [previewVoice, setPreviewVoice] = useState(null); // voiceId currently previewing

  const load = async () => {
    setLoading(true);
    try {
      const data = await ArticlesAPI.list({ limit: 200 });
      const sorted = data
        .map(normalizeArticle)
        .sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0));
      setArticles(sorted);
    } catch (e) {
      toast.error("Failed to load articles");
    } finally {
      setLoading(false);
    }
  };
  const loadStats = async () => {
    try {
      const s = await AudioAPI.stats(200, 7);
      setStats(s);
    } catch { /* silent */ }
  };
  const loadVoices = async () => {
    try {
      const { voices } = await AudioAPI.voices();
      setVoices(voices || []);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); loadStats(); loadVoices(); }, []);

  const playVoiceSample = (voiceId) => {
    setPreviewVoice(voiceId);
    const url = AudioAPI.previewUrl(voiceId);
    const el = new Audio(url);
    el.play().catch(() => {});
    el.addEventListener("ended", () => setPreviewVoice(null));
    el.addEventListener("error", () => {
      setPreviewVoice(null);
      toast.error("Voice sample failed to load");
    });
  };

  const pollStatus = (articleId) => {
    let attempts = 0;
    const iv = setInterval(async () => {
      attempts += 1;
      try {
        const s = await AudioAPI.status(articleId);
        setJobs((j) => ({ ...j, [articleId]: s.status }));
        if (s.status === "done") {
          clearInterval(iv);
          toast.success("Audio ready");
          load();
        } else if (s.status === "failed") {
          clearInterval(iv);
          toast.error("Audio generation failed", { description: s.error });
        }
      } catch { /* ignore */ }
      if (attempts > 60) clearInterval(iv); // give up after 10min
    }, 10000);
  };

  const generate = async (art) => {
    const voice = chosenVoice[art.id] || art.audioVoice || null;
    try {
      setJobs((j) => ({ ...j, [art.id]: "processing" }));
      const r = await AudioAPI.generate(art.id, voice);
      toast.info(`Generating audio · voice: ${r.voice || "default"} · ~${Math.ceil(r.chars / 60)}s expected`);
      pollStatus(art.id);
    } catch (e) {
      setJobs((j) => ({ ...j, [art.id]: "none" }));
      toast.error("Could not start audio generation", { description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (art) => {
    if (!window.confirm(`Delete audio for “${art.title}”?`)) return;
    try {
      await AudioAPI.remove(art.id);
      toast.success("Audio deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const verifyOne = async (art) => {
    setVerifying((v) => ({ ...v, [art.id]: true }));
    try {
      const r = await AudioAPI.healthOne(art.id);
      setHealth((h) => ({ ...h, [art.id]: r }));
      if (r.ok) toast.success(`${art.title.slice(0, 30)}… — all checks passed`);
      else {
        const fails = Object.entries(r.checks || {})
          .filter(([, v]) => !v.pass)
          .map(([k]) => k)
          .join(", ");
        toast.error(`${art.title.slice(0, 30)}… — failed: ${fails}`);
      }
    } catch (e) {
      toast.error("Verify failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setVerifying((v) => ({ ...v, [art.id]: false }));
    }
  };

  const verifyAll = async () => {
    setVerifyingAll(true);
    try {
      const r = await AudioAPI.healthAll();
      const byId = {};
      (r.results || []).forEach((row) => { byId[row.articleId] = row; });
      setHealth(byId);
      if (r.failed === 0) {
        toast.success(`All ${r.total} audio deployments passed every check`);
      } else {
        toast.error(`${r.failed} of ${r.total} audio deployments failed`, {
          description: "Expand each failing article to see which check broke.",
        });
      }
    } catch (e) {
      toast.error("Verify All failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setVerifyingAll(false);
    }
  };

  const togglePremium = async (art) => {
    const nextTier = art.audioTier === "premium" ? "free" : "premium";
    try {
      await ContentControlsAPI.updateMeta(art.id, { audioTier: nextTier });
      toast.success(`Marked "${art.title.slice(0, 30)}…" as ${nextTier.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const setExternalUrl = async (art) => {
    const url = window.prompt(
      `External podcast MP3 URL for "${art.title.slice(0, 60)}"\n\nLeave blank to clear. Used by Path C (podcast host).`,
      art.externalAudioUrl || "",
    );
    if (url === null) return;
    try {
      await ContentControlsAPI.updateMeta(art.id, { externalAudioUrl: url });
      toast.success(url ? "External URL saved" : "External URL cleared");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const setPdfLink = async (art) => {
    const url = window.prompt(
      `Dropbox share link to the premium PDF for "${art.title.slice(0, 60)}"\n\nPaste the "Share" URL (any dl=0/dl=1 form) — it will be normalized automatically. Leave blank to remove.`,
      art.pdfDropboxUrl || "",
    );
    if (url === null) return;
    const label = url
      ? window.prompt(
          `Optional display label (shown on the download button). Blank = "Full Analysis · PDF".`,
          art.pdfLabel || "",
        )
      : "";
    try {
      await ContentControlsAPI.updateMeta(art.id, {
        pdfDropboxUrl: url,
        pdfLabel: label || null,
      });
      toast.success(url ? "PDF link saved" : "PDF link cleared");
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  const audioArticles = articles.filter((a) => Boolean(a.audio) || a.externalAudioUrl);
  const totalPlays = stats?.totalPlays ?? 0;
  const recommendPremium = Boolean(stats?.recommendPremiumMode);

  return (
    <div className="mb-12 border border-neutral-300 bg-white p-6" data-testid="audio-studio-panel">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-semibold inline-flex items-center gap-1.5">
            <Headphones className="w-3.5 h-3.5" /> Audio Studio · OpenAI TTS (onyx)
          </div>
          <h3 className="mt-1 font-serif-display text-[22px] font-bold text-[#0B1F33]">Article Narration</h3>
          <p className="text-[12px] text-neutral-600">Generates a professional MP3 read-through of any article. Runs in the background — takes 30–90 seconds per article.</p>
        </div>
        <div className="flex items-center gap-2">
          <BulkRegenerateAudio articleCount={audioArticles.length} onDone={() => { load(); loadStats(); }} />
          <Button
            onClick={verifyAll}
            disabled={verifyingAll}
            size="sm"
            className="rounded-none bg-[#0B1F33] hover:bg-[#132f57] text-[#FFFFFF] uppercase tracking-wider text-[10px] font-semibold"
            data-testid="verify-all-audio-btn"
          >
            {verifyingAll ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />}
            Verify All Audio
          </Button>
          <Button
            onClick={() => { load(); loadStats(); }}
            variant="outline"
            size="sm"
            className="rounded-none border-neutral-300 hover:border-[#B8E9F1] hover:text-[#0891B2]"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats + Path C readiness banner */}
      {stats && (
        <div
          className={`mb-5 border p-4 flex items-center gap-4 ${recommendPremium ? "border-[#B8E9F1] bg-[#fdf7ec]" : "border-neutral-200 bg-neutral-50"}`}
          data-testid="audio-stats-banner"
        >
          <Radio className={`w-5 h-5 shrink-0 ${recommendPremium ? "text-[#0891B2]" : "text-neutral-400"}`} />
          <div className="flex-1 text-[12px] text-neutral-700 leading-snug">
            <div className="font-semibold text-[#0B1F33]">
              {totalPlays.toLocaleString()} total plays · {audioArticles.length} audio articles · window: last {stats.windowDays}d
            </div>
            <div className="mt-0.5">
              {recommendPremium ? (
                <span className="text-[#8a5a1a] font-semibold" data-testid="premium-recommend">
                  {stats.articlesOverThreshold} article(s) crossed the {stats.premiumThreshold}-play threshold — Path C (premium/podcast host) is ready to activate.
                </span>
              ) : (
                <span className="text-neutral-600">
                  Path C activation threshold: {stats.premiumThreshold} plays in {stats.windowDays}d per article. Not yet reached — audio stays free.
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Voice Library — preview each OpenAI TTS voice before assigning it */}
      {voices.length > 0 && (
        <div className="mb-5 border border-neutral-200 bg-neutral-50 p-4" data-testid="voice-library">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-semibold mb-2 flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5" /> Voice Library — 6 voices available
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {voices.map((v) => {
              const active = previewVoice === v.id;
              const genderColor = v.gender === "female" ? "text-[#a04b6f]" : v.gender === "male" ? "text-[#0B1F33]" : "text-neutral-600";
              return (
                <button
                  key={v.id}
                  onClick={() => playVoiceSample(v.id)}
                  className={`border p-2 text-left transition-colors ${active ? "border-[#B8E9F1] bg-[#fdf7ec]" : "border-neutral-200 bg-white hover:border-[#B8E9F1]"}`}
                  data-testid={`voice-preview-${v.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="font-serif-display font-bold text-[13px] text-[#0B1F33]">{v.label}</span>
                    {active ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin text-[#0891B2]" />
                    ) : (
                      <PlayCircle className="w-3.5 h-3.5 text-neutral-400" />
                    )}
                  </div>
                  <div className={`text-[9px] uppercase tracking-wider font-semibold ${genderColor}`}>{v.gender}</div>
                  <div className="text-[10px] text-neutral-600 leading-tight mt-0.5">{v.character}</div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-8 flex items-center justify-center text-neutral-500">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading articles…
        </div>
      ) : (
        <ul className="divide-y divide-neutral-200">
          {articles.map((a) => {
            const jobStatus = jobs[a.id];
            const busy = jobStatus === "processing";
            const hasAudio = Boolean(a.audio);
            const h = health[a.id];
            const verifyBusy = verifying[a.id];
            const isPremium = a.audioTier === "premium";
            return (
              <li key={a.id} className="py-3 flex items-center gap-3 flex-wrap" data-testid={`audio-row-${a.slug}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display font-semibold text-[15px] text-[#0B1F33] truncate flex items-center gap-2">
                    {a.title}
                    {isPremium && (
                      <span className="text-[9px] tracking-[0.25em] uppercase bg-[#B8E9F1] text-[#0B1F33] px-1.5 py-0.5 font-mono-tick font-semibold" data-testid={`premium-badge-${a.slug}`}>
                        Premium
                      </span>
                    )}
                  </div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-wider text-neutral-500 flex flex-wrap items-center gap-x-2">
                    <span>{a.category}</span>
                    <span className="text-neutral-300">·</span>
                    <span>{a.publishedAt ? new Date(a.publishedAt).toLocaleDateString() : "—"}</span>
                    {hasAudio && <span className="text-emerald-700 font-semibold">✓ audio</span>}
                    {a.audioVoice && (
                      <span
                        className="text-[#0B1F33] font-mono-tick font-semibold"
                        data-testid={`audio-voice-${a.slug}`}
                      >
                        voice: {a.audioVoice}
                      </span>
                    )}
                    {busy && <span className="text-[#0891B2] font-semibold">generating…</span>}
                    <span className="text-neutral-300">·</span>
                    <span className="font-mono-tick text-[#0B1F33]" data-testid={`plays-${a.slug}`}>
                      {(a.plays ?? 0).toLocaleString()} plays
                    </span>
                    {h && (
                      <>
                        <span className="text-neutral-300">·</span>
                        {h.ok ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold" data-testid={`health-ok-${a.slug}`}>
                            <ShieldCheck className="w-3 h-3" /> healthy
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700 font-semibold" data-testid={`health-fail-${a.slug}`} title={Object.entries(h.checks || {}).filter(([, v]) => !v.pass).map(([k, v]) => `${k}: ${v.detail}`).join(" | ")}>
                            <ShieldAlert className="w-3 h-3" /> {Object.values(h.checks || {}).filter((v) => !v.pass).length} check(s) failed
                          </span>
                        )}
                      </>
                    )}
                  </div>
                  {/* Failure detail row when health failed */}
                  {h && !h.ok && (
                    <div className="mt-1.5 text-[10px] text-red-700 bg-red-50 border border-red-200 px-2 py-1 font-mono-tick">
                      {Object.entries(h.checks || {}).filter(([, v]) => !v.pass).map(([k, v]) => (
                        <div key={k}><span className="font-semibold">{k}:</span> {v.detail}</div>
                      ))}
                    </div>
                  )}
                </div>

                {hasAudio && (
                  <Button
                    size="sm"
                    onClick={() => verifyOne(a)}
                    variant="outline"
                    disabled={verifyBusy}
                    className="rounded-none border-neutral-300 hover:border-[#B8E9F1] hover:text-[#0891B2]"
                    title="Run all 6 audio health checks"
                    data-testid={`verify-btn-${a.slug}`}
                  >
                    {verifyBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                    {!verifyBusy && "Verify"}
                  </Button>
                )}
                {hasAudio && (
                  <Button
                    size="sm"
                    onClick={() => setPlaying(a)}
                    variant="ghost"
                    className="rounded-none hover:bg-neutral-200"
                    title="Preview"
                  >
                    <PlayCircle className="w-4 h-4 mr-1" /> Preview
                  </Button>
                )}
                <select
                  value={chosenVoice[a.id] || a.audioVoice || ""}
                  onChange={(e) => setChosenVoice((cv) => ({ ...cv, [a.id]: e.target.value }))}
                  disabled={busy}
                  className="text-[11px] tracking-wider uppercase font-mono-tick border border-neutral-300 bg-white px-2 py-1.5 rounded-none hover:border-[#B8E9F1] focus:border-[#B8E9F1] focus:outline-none"
                  data-testid={`voice-picker-${a.slug}`}
                  title="Voice to use on next Generate"
                >
                  <option value="">Default (onyx)</option>
                  {voices.map((v) => (
                    <option key={v.id} value={v.id}>{v.label} · {v.gender}</option>
                  ))}
                </select>
                <Button
                  size="sm"
                  onClick={() => generate(a)}
                  disabled={busy}
                  className="rounded-none bg-[#0B1F33] hover:bg-[#132f57] text-[#FFFFFF] uppercase tracking-wider text-[10px] font-semibold"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (hasAudio ? "Regenerate" : "Generate")}
                </Button>
                {hasAudio && (
                  <Button
                    size="sm"
                    onClick={() => togglePremium(a)}
                    variant="ghost"
                    className={`rounded-none ${isPremium ? "text-[#0891B2] hover:text-[#0B1F33]" : "text-neutral-500 hover:text-[#0891B2]"}`}
                    title={isPremium ? "Currently premium — click to make free" : "Mark as premium (Path C)"}
                    data-testid={`premium-toggle-${a.slug}`}
                  >
                    {isPremium ? <Unlock className="w-3.5 h-3.5" /> : <Lock className="w-3.5 h-3.5" />}
                  </Button>
                )}
                {hasAudio && (
                  <Button
                    size="sm"
                    onClick={() => setExternalUrl(a)}
                    variant="ghost"
                    className="rounded-none text-neutral-500 hover:text-[#0B1F33]"
                    title={a.externalAudioUrl ? `External URL: ${a.externalAudioUrl}` : "Set podcast host MP3 URL (Path C)"}
                    data-testid={`external-url-${a.slug}`}
                  >
                    <Radio className="w-3.5 h-3.5" />
                  </Button>
                )}
                {hasAudio && (
                  <Button
                    size="sm"
                    onClick={() => setPdfLink(a)}
                    variant="ghost"
                    className={`rounded-none ${a.pdfDropboxUrl ? "text-[#0891B2]" : "text-neutral-500"} hover:text-[#0B1F33]`}
                    title={a.pdfDropboxUrl ? `Premium PDF: ${a.pdfDropboxUrl}` : "Attach premium Dropbox PDF"}
                    data-testid={`pdf-link-${a.slug}`}
                  >
                    <FileDown className="w-3.5 h-3.5" />
                  </Button>
                )}
                {hasAudio && (
                  <Button
                    size="sm"
                    onClick={() => remove(a)}
                    variant="ghost"
                    className="rounded-none text-neutral-500 hover:text-red-600 hover:bg-red-50"
                    title="Delete audio"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </li>
            );
          })}
          {articles.length === 0 && (
            <li className="py-8 text-center text-sm text-neutral-500">No articles yet.</li>
          )}
        </ul>
      )}

      <AudioPlayerModal
        open={Boolean(playing)}
        article={playing}
        onClose={() => setPlaying(null)}
      />
    </div>
  );
};

const ContentControlsPanel = () => {
  const [articles, setArticles] = useState([]);
  const [archived, setArchived] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({}); // articleId -> action string
  const [tab, setTab] = useState("live"); // 'live' | 'archived'

  const load = async () => {
    setLoading(true);
    try {
      const [live, arch] = await Promise.all([
        ArticlesAPI.list({ limit: 200 }),
        ContentControlsAPI.listArchived().catch(() => ({ items: [], total: 0 })),
      ]);
      const sorted = live
        .map(normalizeArticle)
        .sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0));
      setArticles(sorted);
      setArchived(arch.items || []);
    } catch {
      toast.error("Failed to load content");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const withBusy = async (id, key, fn) => {
    setBusy((b) => ({ ...b, [id]: key }));
    try { await fn(); } finally { setBusy((b) => ({ ...b, [id]: null })); }
  };

  const togglePin = async (a, newRank) => {
    await withBusy(a.id, "pin", async () => {
      try {
        await ContentControlsAPI.updateMeta(a.id, { pinnedRank: newRank });
        toast.success(newRank === 0 ? "Unpinned" : `Pinned to slot ${newRank}`);
        load();
      } catch (e) {
        toast.error("Pin update failed");
      }
    });
  };

  const toggleExempt = async (a) => {
    await withBusy(a.id, "exempt", async () => {
      try {
        const newVal = !a.archiveExempt;
        await ContentControlsAPI.updateMeta(a.id, { archiveExempt: newVal });
        toast.success(newVal ? "Protected from auto-archive" : "Protection removed");
        load();
      } catch { toast.error("Update failed"); }
    });
  };

  const bump = async (a) => {
    if (!window.confirm(`Bump "${a.title}" to the top of the wire? (This resets its post time to now.)`)) return;
    await withBusy(a.id, "bump", async () => {
      try {
        await ContentControlsAPI.bump(a.id);
        toast.success("Bumped to top");
        load();
      } catch { toast.error("Bump failed"); }
    });
  };

  const archiveNow = async (a) => {
    if (!window.confirm(`Archive "${a.title}" to Dropbox immediately and remove from the live wire? You can restore it later from the Archived tab.`)) return;
    await withBusy(a.id, "archive", async () => {
      try {
        await ContentControlsAPI.archiveNow(a.id);
        toast.success("Archived to Dropbox");
        load();
      } catch (e) {
        toast.error("Archive failed", { description: e?.response?.data?.detail });
      }
    });
  };

  const restore = async (row) => {
    const bumpChoice = window.confirm(
      `Restore "${row.title}" to the live wire?\n\nOK → restore and BUMP to today (top of wire)\nCancel → restore with ORIGINAL publish date`
    );
    // We'll interpret confirm=OK as bumpToNow=true; a "cancel" from the confirm dialog would abort. So swap: use two prompts.
    // Simpler: always bump for now — user asked for that as default.
    try {
      await ContentControlsAPI.restore(row.id, true);
      toast.success("Restored to top of wire");
      load();
    } catch (e) {
      toast.error("Restore failed", { description: e?.response?.data?.detail });
    }
  };

  const PinSelect = React.useCallback(({ a }) => {
    const cur = a.pinnedRank || 0;
    return (
      <select
        value={cur}
        disabled={busy[a.id] === "pin"}
        onChange={(e) => togglePin(a, parseInt(e.target.value, 10))}
        data-testid={`pin-select-${a.slug}`}
        className="border border-neutral-300 bg-white text-[11px] uppercase tracking-wider px-2 py-1 rounded-none focus:outline-none focus:border-[#B8E9F1]"
      >
        <option value={0}>Not pinned</option>
        {[1,2,3,4,5,6,7,8,9,10].map((n) => (
          <option key={n} value={n}>Pin #{n}</option>
        ))}
      </select>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy]);

  return (
    <div className="mb-12 border border-neutral-300 bg-white p-6" data-testid="content-controls-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#0891B2] font-semibold inline-flex items-center gap-1.5">
            <Pin className="w-3.5 h-3.5" /> Content Controls
          </div>
          <h3 className="mt-1 font-serif-display text-[22px] font-bold text-[#0B1F33]">Pin · Exempt · Archive · Restore</h3>
          <p className="text-[12px] text-neutral-600">Pin an article to a top-10 slot. Exempt it from the 21-day auto-archive. Archive on demand. Restore anything back from Dropbox.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTab("live")}
            data-testid="cc-tab-live"
            className={`px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold border ${tab === "live" ? "bg-[#0B1F33] text-[#FFFFFF] border-[#0B1F33]" : "border-neutral-300 hover:border-[#B8E9F1] hover:text-[#0891B2]"}`}
          >
            Live ({articles.length})
          </button>
          <button
            onClick={() => setTab("archived")}
            data-testid="cc-tab-archived"
            className={`px-3 py-1.5 text-[10px] tracking-[0.25em] uppercase font-semibold border ${tab === "archived" ? "bg-[#0B1F33] text-[#FFFFFF] border-[#0B1F33]" : "border-neutral-300 hover:border-[#B8E9F1] hover:text-[#0891B2]"}`}
          >
            Archived ({archived.length})
          </button>
          <Button
            onClick={load}
            variant="outline"
            size="sm"
            className="rounded-none border-neutral-300 hover:border-[#B8E9F1] hover:text-[#0891B2]"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="py-8 flex items-center justify-center text-neutral-500">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…
        </div>
      ) : tab === "live" ? (
        <ul className="divide-y divide-neutral-200">
          {articles.map((a) => {
            const pinned = a.pinnedRank && a.pinnedRank > 0;
            return (
              <li key={a.id} className="py-3 grid grid-cols-[1fr_auto] gap-4 items-center" data-testid={`cc-row-${a.slug}`}>
                <div className="min-w-0">
                  <div className="font-serif-display font-semibold text-[15px] text-[#0B1F33] truncate flex items-center gap-2">
                    {pinned && <Pin className="w-3.5 h-3.5 text-[#0891B2]" />}
                    {a.archiveExempt && <Lock className="w-3.5 h-3.5 text-emerald-700" />}
                    <span className="truncate">{a.title}</span>
                  </div>
                  <div className="mt-0.5 text-[10px] uppercase tracking-wider text-neutral-500">
                    {a.category} · {a.publishedAt ? new Date(a.publishedAt).toLocaleString() : "—"}
                    {pinned && <span className="ml-2 text-[#0891B2] font-semibold">📌 Pin #{a.pinnedRank}</span>}
                    {a.archiveExempt && <span className="ml-2 text-emerald-700 font-semibold">🔒 exempt</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <PinSelect a={a} />
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleExempt(a)}
                    disabled={busy[a.id] === "exempt"}
                    data-testid={`exempt-toggle-${a.slug}`}
                    className={`rounded-none border ${a.archiveExempt ? "border-emerald-700 text-emerald-700" : "border-neutral-300 text-neutral-700"} hover:border-[#B8E9F1] hover:text-[#0891B2] uppercase tracking-widest text-[10px] px-2 h-8`}
                    title={a.archiveExempt ? "Currently protected — click to allow auto-archive" : "Protect from 21-day auto-archive"}
                  >
                    {a.archiveExempt ? <Lock className="w-3 h-3 mr-1" /> : <Unlock className="w-3 h-3 mr-1" />}
                    {a.archiveExempt ? "Exempt" : "Protect"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => bump(a)}
                    disabled={busy[a.id] === "bump"}
                    data-testid={`bump-btn-${a.slug}`}
                    className="rounded-none border border-neutral-300 hover:border-[#B8E9F1] hover:text-[#0891B2] uppercase tracking-widest text-[10px] px-2 h-8"
                    title="Reset post time to now (bumps to top of wire)"
                  >
                    <ArrowUp className="w-3 h-3 mr-1" /> Bump
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => archiveNow(a)}
                    disabled={busy[a.id] === "archive"}
                    data-testid={`archive-now-${a.slug}`}
                    className="rounded-none border border-neutral-300 hover:border-red-500 hover:text-red-600 uppercase tracking-widest text-[10px] px-2 h-8"
                    title="Send to Dropbox archive immediately and remove from live wire"
                  >
                    <Send className="w-3 h-3 mr-1" /> Archive Now
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      ) : (
        <ul className="divide-y divide-neutral-200">
          {archived.length === 0 && (
            <li className="py-8 text-center text-sm text-neutral-500">
              No archived articles yet. Articles auto-archive after 21 days (unless exempt).
            </li>
          )}
          {archived.map((row) => (
            <li key={row.id} className="py-3 flex items-center gap-4" data-testid={`archived-row-${row.slug}`}>
              <div className="flex-1 min-w-0">
                <div className="font-serif-display font-semibold text-[15px] text-[#0B1F33] truncate">{row.title}</div>
                <div className="mt-0.5 text-[10px] uppercase tracking-wider text-neutral-500">
                  {row.category || "—"} · originally {row.publishedAt ? new Date(row.publishedAt).toLocaleDateString() : "?"}
                  · archived {row.archivedAt ? new Date(row.archivedAt).toLocaleDateString() : "?"}
                  {row.manualArchive && <span className="ml-2 text-[#0891B2]">manual</span>}
                </div>
                <div className="text-[10px] text-neutral-400 font-mono-tick truncate">{row.dropboxPath}</div>
              </div>
              <Button
                size="sm"
                onClick={() => restore(row)}
                data-testid={`restore-btn-${row.slug}`}
                className="rounded-none bg-[#0B1F33] hover:bg-[#132f57] text-[#FFFFFF] uppercase tracking-widest text-[10px] px-3 h-8 font-semibold"
              >
                <Undo2 className="w-3 h-3 mr-1.5" /> Restore
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DropZone = ({ label, hint, icon: Icon, accept, file, onFile }) => {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 font-semibold">{label}</Label>
      <label className="mt-2 flex flex-col items-center justify-center border-2 border-dashed border-neutral-300 hover:border-[#B8E9F1] hover:bg-white transition-colors cursor-pointer p-8 bg-neutral-50">
        <Icon className="w-8 h-8 text-[#0891B2]" />
        <div className="mt-3 text-sm text-neutral-700 font-medium">{file ? file.name : "Click or drag file to upload"}</div>
        <div className="mt-1 text-[11px] text-neutral-500">{hint}</div>
        <input type="file" accept={accept} className="hidden" onChange={(e) => onFile(e.target.files?.[0] || null)} />
      </label>
    </div>
  );
};

const CoverPicker = ({ value, onChange }) => (
  <div>
    <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 font-semibold flex items-center gap-1.5">
      <ImageIcon className="w-3.5 h-3.5" /> Cover Image (choose one)
    </Label>
    <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-2">
      {COVER_LIBRARY.map((url) => (
        <button
          type="button"
          key={url}
          onClick={() => onChange(url)}
          className={`relative aspect-[4/3] overflow-hidden border-2 transition-all ${value === url ? 'border-[#B8E9F1]' : 'border-transparent hover:border-neutral-400'}`}
        >
          <img src={url} alt="cover" className="w-full h-full object-cover" loading="lazy" />
          {value === url && (
            <span className="absolute top-1 right-1 w-5 h-5 rounded-full bg-[#B8E9F1] text-[#0B1F33] flex items-center justify-center">
              <Check className="w-3.5 h-3.5" />
            </span>
          )}
        </button>
      ))}
    </div>
    <div className="mt-2 text-[11px] text-neutral-500">Or upload a custom image below.</div>
  </div>
);

const AdminGate = ({ children }) => {
  const { user, loading: authLoading, signIn } = useAuth();
  const [check, setCheck] = useState({ loading: true });

  const runCheck = async () => {
    setCheck({ loading: true });
    try {
      const r = await api.get("/auth/admin-check", { withCredentials: true });
      setCheck({ loading: false, ...r.data });
    } catch {
      setCheck({ loading: false, isAdmin: false });
    }
  };

  useEffect(() => {
    if (!authLoading) runCheck();
  }, [authLoading, user]);

  const bootstrap = async () => {
    try {
      await api.post("/auth/admin-bootstrap", {}, { withCredentials: true });
      toast.success("Admin access granted");
      runCheck();
    } catch (e) {
      toast.error("Bootstrap failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  if (authLoading || check.loading) {
    return (
      <div>
        <Ticker /><Header />
        <div className="max-w-[900px] mx-auto px-6 py-32 flex items-center justify-center text-neutral-500">
          <Loader2 className="w-6 h-6 animate-spin mr-3 text-[#0891B2]" /> Verifying admin access…
        </div>
        <Footer />
      </div>
    );
  }

  if (!user) {
    return (
      <div>
        <Ticker /><Header />
        <main className="max-w-[560px] mx-auto px-6 py-24 text-center">
          <ShieldAlert className="w-10 h-10 text-[#0891B2] mx-auto" />
          <h1 className="mt-4 font-serif-display text-[32px] font-bold text-[#0B1F33]">Admin sign-in required</h1>
          <p className="mt-3 text-[15px] text-neutral-700">
            The Vexent Wire back office is restricted. Sign in with the
            authorized owner account to continue.
          </p>
          <button
            onClick={signIn}
            data-testid="admin-gate-signin"
            className="mt-6 inline-flex items-center gap-2 bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33] px-6 py-3 text-[12px] tracking-[0.28em] uppercase font-condensed font-bold transition-colors"
          >
            Continue with Google
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  if (!check.isAdmin && !check.adminSeatsFilled) {
    return (
      <div>
        <Ticker /><Header />
        <main className="max-w-[560px] mx-auto px-6 py-24 text-center">
          <ShieldCheck className="w-10 h-10 text-[#0891B2] mx-auto" />
          <h1 className="mt-4 font-serif-display text-[32px] font-bold text-[#0B1F33]">Claim ownership</h1>
          <p className="mt-3 text-[15px] text-neutral-700">
            No admin has been assigned yet. As the site owner, click below to
            claim the sole admin seat. This is a one-time action.
          </p>
          <div className="mt-4 text-[12px] font-mono-tick text-neutral-500 truncate">Signing in as: {check.email}</div>
          <button
            onClick={bootstrap}
            data-testid="admin-bootstrap-btn"
            className="mt-6 inline-flex items-center gap-2 bg-[#0B1F33] hover:bg-[#132f57] text-[#FFFFFF] px-6 py-3 text-[12px] tracking-[0.28em] uppercase font-condensed font-bold transition-colors"
          >
            Claim Admin Access
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  if (!check.isAdmin) {
    return (
      <div>
        <Ticker /><Header />
        <main className="max-w-[560px] mx-auto px-6 py-24 text-center">
          <ShieldAlert className="w-10 h-10 text-red-600 mx-auto" />
          <h1 className="mt-4 font-serif-display text-[32px] font-bold text-[#0B1F33]">Access denied</h1>
          <p className="mt-3 text-[15px] text-neutral-700">
            Your account ({check.email}) is not authorized to access the back
            office. Contact the site owner if this is an error.
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  return children;
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: "",
    dek: "",
    category: "markets",
    tags: "",
    body: "",
    videoUrl: "",
    readTime: 5,
    featured: false,
    publishNow: true,
    image: COVER_LIBRARY[0],
  });
  const [bodyFile, setBodyFile] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error("Headline is required."); return; }
    if (!form.category) { toast.error("Please choose a section."); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("dek", form.dek);
      fd.append("category", form.category);
      fd.append("tags", form.tags);
      fd.append("body", form.body);
      fd.append("videoUrl", form.videoUrl);
      fd.append("image", form.image);
      fd.append("readTime", String(form.readTime));
      fd.append("featured", String(form.featured));
      fd.append("status", form.publishNow ? "published" : "draft");
      if (bodyFile) fd.append("bodyFile", bodyFile);
      if (audioFile) fd.append("audioFile", audioFile);
      if (videoFile) fd.append("videoFile", videoFile);
      if (imageFile) fd.append("imageFile", imageFile);

      const created = await ArticlesAPI.create(fd);
      toast.success(form.publishNow ? "Article published." : "Draft saved.", {
        description: `Slug: ${created.slug}`,
      });
      if (form.publishNow) {
        setTimeout(() => navigate(`/article/${created.slug}`), 600);
      }
    } catch (err) {
      toast.error("Publish failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Ticker />
      <Header />

      <main className="max-w-[1200px] mx-auto px-6 py-12">
        <div className="pb-6 border-b-2 border-[#0B1F33] mb-10">
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#0891B2] font-semibold">Editor Portal</div>
          <h1 className="mt-2 font-serif-display text-[44px] md:text-[54px] font-bold text-[#0B1F33] leading-none">Contribute an Article</h1>
          <p className="mt-3 text-[15px] text-neutral-600 max-w-2xl">
            Upload research pieces in PDF, HTML, markdown or paste plain text. Attach optional audio narration and video companions. Choose an editorial cover.
          </p>
        </div>

        <StatsPanel />
        <MembersPanel />
        <ArchivePanel />
        <ContentControlsPanel />
        <AudioStudioPanel />

        <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 font-semibold">Headline</Label>
              <Input required value={form.title} onChange={(e) => update("title", e.target.value)} placeholder="The Quiet Repricing of Sovereign Risk" className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus:border-[#B8E9F1] text-lg" />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 font-semibold">Dek / Subhead</Label>
              <Textarea rows={2} value={form.dek} onChange={(e) => update("dek", e.target.value)} placeholder="A one-sentence summary of the piece…" className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus:border-[#B8E9F1]" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 font-semibold">Section</Label>
                <Select value={form.category} onValueChange={(v) => update("category", v)}>
                  <SelectTrigger className="mt-2 rounded-none border-neutral-300 focus:ring-0 focus:border-[#B8E9F1]">
                    <SelectValue placeholder="Select a section" />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 font-semibold">Read Time (min)</Label>
                <Input type="number" min={1} max={60} value={form.readTime} onChange={(e) => update("readTime", parseInt(e.target.value || "5"))} className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus:border-[#B8E9F1]" />
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 font-semibold">Tags (comma separated)</Label>
                <Input value={form.tags} onChange={(e) => update("tags", e.target.value)} placeholder="Gold, Fed, Term Premium" className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus:border-[#B8E9F1]" />
              </div>
            </div>

            <CoverPicker value={imageFile ? null : form.image} onChange={(v) => { setImageFile(null); update("image", v); }} />

            <DropZone label="Custom Cover Image (optional, overrides picker)" hint="JPG or PNG · Recommended 1600×900" icon={ImageIcon} accept="image/*" file={imageFile} onFile={setImageFile} />

            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 font-semibold">Article Body (paste text or markdown)</Label>
              <Textarea rows={8} value={form.body} onChange={(e) => update("body", e.target.value)} placeholder="Paste your article text here… blank lines separate paragraphs." className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus:border-[#B8E9F1] font-serif-display text-[16px]" />
            </div>

            <DropZone label="Or upload source file (PDF, HTML, MD, DOCX, TXT)" hint="Max 25MB · Renders inline" icon={FileText} accept=".pdf,.html,.md,.docx,.txt" file={bodyFile} onFile={setBodyFile} />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <DropZone label="Audio Narration (optional)" hint="MP3 / WAV · Adds inline player" icon={Music} accept="audio/*" file={audioFile} onFile={setAudioFile} />
              <DropZone label="Video Companion (optional)" hint="MP4 file, or YouTube URL below" icon={Video} accept="video/*" file={videoFile} onFile={setVideoFile} />
            </div>

            <div>
              <Label className="text-[11px] uppercase tracking-[0.2em] text-neutral-600 font-semibold">Video URL (YouTube / Vimeo)</Label>
              <Input value={form.videoUrl} onChange={(e) => update("videoUrl", e.target.value)} placeholder="https://youtube.com/watch?v=…" className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus:border-[#B8E9F1]" />
            </div>

            <div className="flex items-center gap-8 pt-2">
              <div className="flex items-center gap-3">
                <Switch checked={form.featured} onCheckedChange={(v) => update("featured", v)} />
                <span className="text-[12px] uppercase tracking-wider text-neutral-700 font-semibold">Feature on homepage hero</span>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={form.publishNow} onCheckedChange={(v) => update("publishNow", v)} />
                <span className="text-[12px] uppercase tracking-wider text-neutral-700 font-semibold">Publish immediately</span>
              </div>
            </div>
          </div>

          <aside className="space-y-6">
            <div className="bg-[#0B1F33] text-[#D7DEE5] p-6 sticky top-4">
              <UploadCloud className="w-8 h-8 text-[#0891B2]" />
              <h3 className="mt-3 font-serif-display text-[22px] font-bold">Publish Pipeline</h3>
              <ol className="mt-4 space-y-3 text-[13px] text-[#D7DEE5]/85">
                <li className="flex gap-2"><span className="text-[#0891B2] font-mono-tick">01</span> Fill headline, dek, section</li>
                <li className="flex gap-2"><span className="text-[#0891B2] font-mono-tick">02</span> Pick cover or upload custom</li>
                <li className="flex gap-2"><span className="text-[#0891B2] font-mono-tick">03</span> Attach body / audio / video</li>
                <li className="flex gap-2"><span className="text-[#0891B2] font-mono-tick">04</span> Publish or save draft</li>
              </ol>
              <Button type="submit" disabled={submitting} className="mt-6 w-full rounded-none bg-[#B8E9F1] hover:bg-[#9FDDE7] text-[#0B1F33] uppercase tracking-wider text-[12px] font-semibold h-12">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing…</> : (
                  <>{form.publishNow ? "Publish Article" : "Save Draft"} <ArrowRight className="w-4 h-4 ml-2" /></>
                )}
              </Button>
            </div>
            <div className="border border-neutral-300 p-6">
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#0891B2] font-semibold">Coming Soon</div>
              <h4 className="mt-2 font-serif-display text-[18px] font-bold text-[#0B1F33]">Paid Tiers & Payouts</h4>
              <p className="mt-2 text-[13px] text-neutral-600 leading-relaxed">Premium content gating and contributor payouts will unlock in the next release.</p>
            </div>
          </aside>
        </form>
      </main>

      <Footer />
    </div>
  );
};

export default function AdminPageGated() {
  return (
    <AdminGate>
      <AdminPage />
    </AdminGate>
  );
}
